export type LotAvailability =
	| { kind: 'soldout' }
	| { kind: 'low'; left: number }
	| { kind: 'available' }

/** A slot lot's inventory, narrowed to the fields availability derives from. */
type SlotsInventory = {
	type: 'slots'
	items: Array<{ name: string; status: string }>
}
/** A stock lot's inventory, narrowed to the fields availability derives from. */
type StockInventory = {
	type: 'stock'
	items: Array<{ name: string; availableQuantity: number }>
}
export type Inventory = SlotsInventory | StockInventory

/** 1-2 free units reads as urgency ("Осталось N") per the feed design. */
const LOW_THRESHOLD = 2

/**
 * Units left across either mode: free slots for slot lots,
 * `maxQuantity - soldQuantity` (i.e. `availableQuantity`) summed for stock.
 */
function unitsLeft(inventory: Inventory): number {
	return inventory.type === 'slots'
		? inventory.items.filter((s) => s.status === 'free').length
		: inventory.items.reduce((sum, i) => sum + i.availableQuantity, 0)
}

export function getLotAvailability(inventory: Inventory): LotAvailability {
	const left = unitsLeft(inventory)
	if (left === 0) return { kind: 'soldout' }
	if (left <= LOW_THRESHOLD) return { kind: 'low', left }
	return { kind: 'available' }
}

/**
 * Distinct variant chips in first-seen order; a variant is available if any
 * of its slots is free (slots) or it has units left (stock).
 */
export function getVariantChips(
	inventory: Inventory,
): Array<{ name: string; available: boolean }> {
	if (inventory.type === 'stock')
		return inventory.items.map((i) => ({
			name: i.name,
			available: i.availableQuantity > 0,
		}))
	const map = new Map<string, boolean>()
	for (const s of inventory.items)
		map.set(s.name, (map.get(s.name) ?? false) || s.status === 'free')
	return [...map].map(([name, available]) => ({ name, available }))
}
